// Terms of Use — Project Radar AR
// Each language object exports the same shape so the renderer can iterate
// through the structured articles in a typesafe way.

const make = (title, intro, articles, footer) => ({ title, intro, articles, footer });

const ARTICLES_DE = [
  {
    title: "Art. 1 Gegenstand und Geltungsbereich",
    items: [
      "Project Radar AR (nachfolgend „Plattform“) ist ein KI-natives Ökosystem zur Transformation unstrukturierter Text-Briefings in technische Projektstrukturen, Kalkulationen und 3D-Modelle.",
      "Diese Nutzungsbedingungen regeln das Rechtsverhältnis zwischen dem Betreiber der Plattform und den Nutzern (Auftraggeber, Auftragnehmer, Agenturen).",
      "Mit der Nutzung der Plattform erkennt der Nutzer diese Bedingungen vollumfänglich an.",
    ],
  },
  {
    title: "Art. 2 Leistungsbeschreibung und Funktionalität",
    lead: "Die Plattform stellt folgende Kernfunktionen bereit:",
    items: [
      "KI-Scoping: Generierung von Low-Fidelity-Prototypen, Seitenstrukturen und technischen Features basierend auf Texteingaben.",
      "Kalkulations-Engine: Automatische Schätzung von Arbeitsstunden, Komplexitäts-Scores und Technologie-Stacks.",
      "Spatial Computing: Bereitstellung einer Augmented Reality (AR)-Vorschau zur physischen Visualisierung von Projektmodellen im Raum.",
      "Smart Contracts: Automatisierte Erstellung von Vertragsentwürfen basierend auf dem generierten Projektumfang.",
      "Kollaborations-Hub: Bereitstellung eines Live-Chats für den direkten Austausch zwischen Projektbeteiligten.",
    ],
  },
  {
    title: "Art. 3 Haftungsausschluss für KI-Resultate",
    items: [
      "Die Plattform löst das Marktproblem ineffizienter Discovery-Phasen durch den Einsatz generativer KI. Die Resultate (Analysen, Zeitpläne, 3D-Modelle) basieren auf probabilistischen Modellen.",
      "Haftungsausschluss: Project Radar AR übernimmt keinerlei Haftung für die Richtigkeit, Genauigkeit oder Vollständigkeit der generierten Daten.",
      "Eigenverantwortung: Die Verwendung der Analyseergebnisse sowie die darauf basierende Projektdurchführung liegen in der alleinigen Verantwortung des Nutzers.",
      "Risikoanalyse: Nutzer sind verpflichtet, die von der KI identifizierten Risiken (z. B. Performance, Compliance, API-Quoten) eigenständig zu bewerten.",
    ],
  },
  {
    title: "Art. 4 Besondere Bestimmungen für Smart Contracts",
    items: [
      "Die Plattform automatisiert die Vertragserstellung zur Beschleunigung von B2B-Prozessen.",
      "Keine Rechtsberatung: Die Bereitstellung von Vertragsentwürfen stellt keine Rechtsberatung dar.",
      "Prüfungspflicht: Nutzer sind zwingend verpflichtet, generierte Verträge vor Unterzeichnung autonom und unter Beizug qualifizierter Rechtsbeistände (Anwälte, Rechtsdienste) zu prüfen.",
    ],
  },
  {
    title: "Art. 5 Technologische Exzellenz und Verfügbarkeit",
    items: [
      "Die Plattform basiert auf einer High-End-Architektur (Emergent) für maximale Skalierbarkeit.",
      "Ein Anspruch auf eine 100 %ige Verfügbarkeit besteht nicht. Wartungsfenster oder Ausfälle durch Drittanbieter (z. B. Google Maps API, Stripe) können die Funktionalität einschränken.",
    ],
  },
  {
    title: "Art. 6 Geistiges Eigentum und Full Ownership",
    items: [
      "Sofern nicht anders vereinbart, verbleiben alle Urheberrechte an der zugrundeliegenden Plattform-Technologie beim Betreiber.",
      "Im Falle einer vollständigen Übernahme (Asset Deal) wird eine vollständige GitHub-Integration mit dokumentiertem Code zur Weiterentwicklung gewährleistet.",
    ],
  },
  {
    title: "Art. 7 Datenschutz und Vertraulichkeit",
    items: [
      "Die Verarbeitung von Projektdaten erfolgt gemäss der separaten Datenschutzerklärung.",
      "Da die Plattform weltweit einsetzbar und mehrsprachig ist (DE, EN, FR, ES), obliegt die Einhaltung lokaler Datenschutzvorschriften (z. B. DSGVO) bei der Abwicklung von Kundenprojekten dem Nutzer.",
    ],
  },
  {
    title: "Art. 8 Schlussbestimmungen",
    items: [
      "Anwendbares Recht: Auf diese Nutzungsbedingungen sowie das gesamte Rechtsverhältnis zwischen dem Betreiber und dem Nutzer ist ausschliesslich Schweizer Recht anwendbar.",
      "Gerichtsstand: Ausschliesslicher Gerichtsstand für alle Streitigkeiten aus oder im Zusammenhang mit dieser Vereinbarung ist Bern, Schweiz.",
      "Salvatorische Klausel: Sollten einzelne Bestimmungen dieser Vereinbarung unwirksam sein, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.",
    ],
  },
];

const ARTICLES_EN = [
  {
    title: "Art. 1 Subject Matter and Scope",
    items: [
      'Project Radar AR (hereinafter "the Platform") is an AI-native ecosystem designed to transform unstructured text briefings into technical project structures, calculations, and 3D models.',
      "These Terms of Use govern the legal relationship between the operator of the Platform and its users, including clients, freelancers, and agencies.",
      "By using the Platform, the user fully acknowledges and accepts these terms.",
    ],
  },
  {
    title: "Art. 2 Description of Services and Functionality",
    lead: "The Platform provides the following core functions:",
    items: [
      "AI Scoping: Generation of low-fidelity prototypes, page structures, and technical features based on text inputs.",
      "Calculation Engine: Automatic estimation of labor hours, complexity scores, and technology stack recommendations.",
      "Spatial Computing: Provision of an Augmented Reality (AR) preview for the physical visualization of project models in space.",
      "Smart Contracts: Automated generation of contract drafts based on the generated project scope.",
      "Collaboration Hub: Integrated live chat for direct exchange between project participants.",
    ],
  },
  {
    title: "Art. 3 Disclaimer for AI Results",
    items: [
      "The Platform addresses the market problem of inefficient discovery phases through generative AI. Results such as analyses, schedules, and 3D models are based on probabilistic models.",
      "Disclaimer: Project Radar AR assumes no liability for the accuracy, precision, or completeness of the generated data.",
      "Personal Responsibility: Use of the analysis results and the resulting project execution are the sole responsibility of the user.",
      "Risk Analysis: Users are required to independently evaluate the risks identified by the AI, such as performance, compliance, and API quotas.",
    ],
  },
  {
    title: "Art. 4 Special Provisions for Smart Contracts",
    items: [
      "The Platform automates contract creation to accelerate B2B processes.",
      "No Legal Advice: The provision of contract drafts does not constitute legal advice.",
      "Duty to Review: Users are strictly required to review generated contracts autonomously and with the assistance of qualified legal counsel (lawyers, legal departments) before signing.",
    ],
  },
  {
    title: "Art. 5 Technological Excellence and Availability",
    items: [
      "The Platform is based on a high-end architecture (Emergent) for maximum scalability.",
      "There is no claim to 100% availability. Maintenance windows or failures by third-party providers (e.g., Google Maps API, Stripe) may restrict functionality.",
    ],
  },
  {
    title: "Art. 6 Intellectual Property and Full Ownership",
    items: [
      "Unless otherwise agreed, all copyrights to the underlying platform technology remain with the operator.",
      "In the event of a full asset deal, complete GitHub integration with documented code for further development is guaranteed.",
    ],
  },
  {
    title: "Art. 7 Data Protection and Confidentiality",
    items: [
      "The processing of project data is carried out in accordance with the separate Privacy Policy.",
      "As the Platform is globally applicable and multilingual (DE, EN, FR, ES), compliance with local data protection regulations (e.g., GDPR) during the processing of customer projects is the responsibility of the user.",
    ],
  },
  {
    title: "Art. 8 Final Provisions",
    items: [
      "Applicable Law: These Terms of Use and the entire legal relationship between the operator and the user are subject exclusively to Swiss Law.",
      "Jurisdiction: The exclusive place of jurisdiction for all disputes arising from or in connection with this agreement is Bern, Switzerland.",
      "Severability Clause: Should individual provisions of this agreement be invalid, the validity of the remaining provisions shall remain unaffected.",
    ],
  },
];

const ARTICLES_FR = [
  {
    title: "Art. 1 Objet et champ d'application",
    items: [
      'Project Radar AR (ci-après "la Plateforme") est un écosystème natif de l\'IA conçu pour transformer des briefings textuels non structurés en structures de projet techniques, calculs et modèles 3D.',
      "Les présentes conditions d'utilisation régissent la relation juridique entre l'exploitant de la Plateforme et ses utilisateurs, notamment les clients, les freelances et les agences.",
      "En utilisant la Plateforme, l'utilisateur reconnaît et accepte pleinement ces conditions.",
    ],
  },
  {
    title: "Art. 2 Description des services et fonctionnalités",
    lead: "La Plateforme propose les fonctions de base suivantes :",
    items: [
      "Scoping par l'IA : Génération de prototypes basse fidélité, de structures de pages et de fonctionnalités techniques basées sur des saisies de texte.",
      "Moteur de calcul : Estimation automatique des heures de travail, des scores de complexité et des recommandations de piles technologiques (Tech-Stack).",
      "Spatial Computing : Mise à disposition d'un aperçu en réalité augmentée (AR) pour la visualisation physique des modèles de projet dans l'espace.",
      "Smart Contracts : Création automatisée de projets de contrat basés sur l'étendue du projet généré.",
      "Hub de collaboration : Chat en direct intégré pour un échange direct entre les participants au projet.",
    ],
  },
  {
    title: "Art. 3 Exclusion de responsabilité pour les résultats de l'IA",
    items: [
      "La Plateforme résout le problème du marché des phases de découverte inefficaces grâce à l'IA générative. Les résultats tels que les analyses, les calendriers et les modèles 3D sont basés sur des modèles probabilistes.",
      "Exclusion de responsabilité : Project Radar AR décline toute responsabilité quant à l'exactitude, la précision ou l'exhaustivité des données générées.",
      "Responsabilité personnelle : L'utilisation des résultats d'analyse ainsi que l'exécution du projet qui en découle relèvent de la seule responsabilité de l'utilisateur.",
      "Analyse des risques : Les utilisateurs sont tenus d'évaluer de manière indépendante les risques identifiés par l'IA, tels que la performance, la conformité et les quotas d'API.",
    ],
  },
  {
    title: "Art. 4 Dispositions particulières pour les Smart Contracts",
    items: [
      "La Plateforme automatise la création de contrats pour accélérer les processus B2B.",
      "Pas de conseil juridique : La fourniture de projets de contrat ne constitue pas un conseil juridique.",
      "Obligation de révision : Les utilisateurs sont impérativement tenus de réviser les contrats générés de manière autonome et avec l'assistance d'un conseiller juridique qualifié (avocats, services juridiques) avant de les signer.",
    ],
  },
  {
    title: "Art. 5 Excellence technologique et disponibilité",
    items: [
      "La Plateforme repose sur une architecture haut de gamme (Emergent) pour une évolutivité maximale.",
      "Un droit à une disponibilité de 100 % n'existe pas. Des fenêtres de maintenance ou des pannes chez des fournisseurs tiers (par ex. Google Maps API, Stripe) peuvent limiter les fonctionnalités.",
    ],
  },
  {
    title: "Art. 6 Propriété intellectuelle et Full Ownership",
    items: [
      "Sauf accord contraire, tous les droits d'auteur sur la technologie de la plateforme sous-jacente restent la propriété de l'exploitant.",
      "En cas de cession complète des actifs (Asset Deal), une intégration complète de GitHub avec un code documenté pour un développement ultérieur est garantie.",
    ],
  },
  {
    title: "Art. 7 Protection des données et confidentialité",
    items: [
      "Le traitement des données relatives au projet s'effectue conformément à la politique de confidentialité distincte.",
      "La Plateforme étant utilisable dans le monde entier et multilingue (DE, EN, FR, ES), le respect des réglementations locales en matière de protection des données (par ex. RGPD) lors du traitement des projets clients incombe à l'utilisateur.",
    ],
  },
  {
    title: "Art. 8 Dispositions finales",
    items: [
      "Droit applicable : Les présentes conditions d'utilisation ainsi que l'ensemble de la relation juridique entre l'exploitant et l'utilisateur sont exclusivement régies par le droit suisse.",
      "For juridique : Le for exclusif pour tous les litiges découlant de ou en rapport avec cet accord est Berne, Suisse.",
      "Clause salvatrice : Si certaines dispositions de cet accord étaient nulles, la validité des autres dispositions n'en serait pas affectée.",
    ],
  },
];

// ES = mirror EN for now (project speaks DE/EN/FR primarily)
const ARTICLES_ES = ARTICLES_EN;

export const TERMS = {
  de: make(
    "Nutzungsbedingungen (Terms of Use)",
    "Diese Nutzungsbedingungen regeln die Nutzung der Plattform Project Radar AR. Bitte lesen Sie sie sorgfältig durch, bevor Sie die Plattform nutzen.",
    ARTICLES_DE,
    {
      acceptCheckbox: "Ich habe die Nutzungsbedingungen gelesen, verstanden und akzeptiere sie vollumfänglich.",
      acceptButton: "Bedingungen akzeptieren und fortfahren",
      declineButton: "Ablehnen",
      readMore: "Vollständig lesen",
      lastUpdated: "Zuletzt aktualisiert",
      printPdf: "Als PDF speichern",
      mustScroll: "Bitte scrollen Sie bis zum Ende der Bedingungen, um zustimmen zu können.",
      requiredAcceptance: "Bevor Sie Project Radar AR nutzen können, müssen Sie unseren Nutzungsbedingungen zustimmen.",
      welcomeTitle: "Willkommen bei Project Radar AR",
    }
  ),
  en: make(
    "Terms of Use",
    "These Terms of Use govern the use of the Project Radar AR platform. Please read them carefully before using the Platform.",
    ARTICLES_EN,
    {
      acceptCheckbox: "I have read, understood and fully accept the Terms of Use.",
      acceptButton: "Accept terms and continue",
      declineButton: "Decline",
      readMore: "Read full version",
      lastUpdated: "Last updated",
      printPdf: "Save as PDF",
      mustScroll: "Please scroll to the bottom of the terms to be able to accept.",
      requiredAcceptance: "Before you can use Project Radar AR, you must agree to our Terms of Use.",
      welcomeTitle: "Welcome to Project Radar AR",
    }
  ),
  fr: make(
    "Conditions d'utilisation",
    "Les présentes conditions d'utilisation régissent l'utilisation de la plateforme Project Radar AR. Veuillez les lire attentivement avant d'utiliser la Plateforme.",
    ARTICLES_FR,
    {
      acceptCheckbox: "J'ai lu, compris et j'accepte pleinement les Conditions d'utilisation.",
      acceptButton: "Accepter et continuer",
      declineButton: "Refuser",
      readMore: "Lire la version complète",
      lastUpdated: "Dernière mise à jour",
      printPdf: "Enregistrer en PDF",
      mustScroll: "Veuillez faire défiler jusqu'à la fin des conditions pour pouvoir accepter.",
      requiredAcceptance: "Avant de pouvoir utiliser Project Radar AR, vous devez accepter nos Conditions d'utilisation.",
      welcomeTitle: "Bienvenue sur Project Radar AR",
    }
  ),
  es: make(
    "Términos de Uso",
    "Estos Términos de Uso rigen el uso de la plataforma Project Radar AR. Por favor, léalos detenidamente antes de utilizar la Plataforma.",
    ARTICLES_ES,
    {
      acceptCheckbox: "He leído, comprendido y acepto plenamente los Términos de Uso.",
      acceptButton: "Aceptar y continuar",
      declineButton: "Rechazar",
      readMore: "Leer la versión completa",
      lastUpdated: "Última actualización",
      printPdf: "Guardar como PDF",
      mustScroll: "Por favor, desplácese hasta el final de los términos para poder aceptar.",
      requiredAcceptance: "Antes de utilizar Project Radar AR, debe aceptar nuestros Términos de Uso.",
      welcomeTitle: "Bienvenido a Project Radar AR",
    }
  ),
};

// Renders a TERMS object as structured JSX
export function TermsBody({ terms }) {
  return (
    <div className="space-y-6">
      {terms.articles.map((art, i) => (
        <section key={i} data-testid={`terms-article-${i + 1}`}>
          <h3 className="font-['Outfit'] text-base font-semibold text-white mb-2">{art.title}</h3>
          {art.lead && <p className="text-sm text-[#A0A0B0] mb-2">{art.lead}</p>}
          <ol className="list-decimal list-inside space-y-1.5 marker:text-[#00F0FF] marker:font-['JetBrains_Mono']">
            {art.items.map((it, j) => (
              <li key={j} className="text-sm text-[#A0A0B0] leading-relaxed pl-1">{it}</li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}
